import React from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Activity, ActivityType, asyncSetTimeout, MeetingInvitationStatus } from "../../../amorg-common";
import { deleteActivity, getMeetingInvitationStatus, sendMeetingInvitation } from "../../../api/activityClientApi";
import { PageHeader } from "../../../designSystem/PageHeader";
import { ToolBarButton, ToolBarButtonProps, ToolBarContainer } from "../../../designSystem/compositeComponents/ButtonToolBar";
import { DeleteConfirmationModal } from "../../../designSystem/compositeComponents/DeleteConfirmationModal";
import { txt } from "../../../txt";
import { ActivityInvitationStatusPanel } from "./ActivityInvitationStatusPanel";
import { DownloadActivityIcsButton } from "./DownloadActivityIcsButton";
import PrintMeetingActivity from "./PrintMeetingActivity";
import { SingleColumnLayout } from "../../_layouts/SingleColumnLayout";

type ActivityHeaderProps =  {
    activity: Activity;
    deleteConfirmationTextInModal?: string;
    onActivityUpdated?: () => void;
    handleClickSendInvitation?: () => void;
    isLoading?: (value:boolean) => void;
    additionalToolBarButtons?:ToolBarButtonProps[];
    hideDeleteButton?: boolean;
}

export function ActivityHeader(props:ActivityHeaderProps){

    const [inDelete, setInDelete] = React.useState(false);
    const [invitationStatus, setInvitationStatus] = React.useState<MeetingInvitationStatus | null>(null);
    

    const navigate = useNavigate();

    React.useEffect(() => {
        async function getInvitationStatus() {
            setInvitationStatus(await getMeetingInvitationStatus(props.activity.id));
            props.isLoading?.(false);
        }
        if(showInvitationButtons)
        {
            getInvitationStatus();
        }
    }, [props.activity]);

    
    async function handleClickSendInvitation()
    {
        if(showInvitationButtons)
        {
            props.isLoading?.(true);
            await asyncSetTimeout(async () => { return; }, 1000);
            sendMeetingInvitation(props.activity.id);
            props.onActivityUpdated?.();
            props.isLoading?.(false);
        }
    }

    async function handleClickDeleteActivity() 
    {
        await deleteActivity(props.activity.id);
        setInDelete(false);
        navigate(-1);
    }
    
    const invitationActivityTypes:ActivityType[] = [
        "WorkgroupMeeting",
        "AnnualWorkgroupStatus",
    ];
    const showInvitationButtons = invitationActivityTypes.includes(props.activity.activityType);

    const printActivityTypes:ActivityType[] = [
        "WorkgroupMeeting",
        "AnnualWorkgroupStatus",
        "SafetyInspection",
    ];
    const showPrintButton = printActivityTypes.includes(props.activity.activityType);

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: txt.getDynamic(`ActivityType_${props.activity.activityType}`)
    });
    
    return (
        <>

        {invitationStatus &&
            <SingleColumnLayout>
                <ActivityInvitationStatusPanel 
                    status={invitationStatus.status}
                    activity={{start: props.activity.start, participantCount: props.activity.participants.length}}
                    onClick={handleClickSendInvitation} />
            </SingleColumnLayout>
        }
        
        <PageHeader
            headerText={txt.getDynamic(`ActivityType_${props.activity.activityType}`)}
            buttons={
                <ToolBarContainer>
                    {props.additionalToolBarButtons && props.additionalToolBarButtons.map((button, index) => {
                        return <ToolBarButton key={index} {...button} />
                    })}
                    {showInvitationButtons &&
                        <ToolBarButton
                            icon="interface_actions_send"
                            tooltip="Send invitation"
                            onClick={ () => handleClickSendInvitation()}
                            variant="text" />
                    }
                    {showInvitationButtons &&
                        <DownloadActivityIcsButton
                            activityId={props.activity.id}
                            icon='calendar_new'
                            toolTipText= 'Tilføj til kalender'
                            variant= 'text' />
                    }
                    {showPrintButton &&
                        <>
                            <ToolBarButton
                                icon='interface_actions_print'
                                tooltip= 'Print'
                                variant= 'text'
                                onClick={printFn}
                            />

                            <PrintMeetingActivity 
                                activity={props.activity}
                                innerRef={printRef} />
                        </> 
                    }
                    
                    {!props.hideDeleteButton &&
                        <>
                            <ToolBarButton
                                icon='interface_actions_delete'
                                tooltip= 'Slet'
                                onClick={ () => setInDelete(true)}
                                variant= 'destructive-text' />

                            <DeleteConfirmationModal
                                showModal={inDelete}
                                buttonText={"Slet " + txt.getDynamic(`ActivityType_${props.activity.activityType}`)}
                                confirmationHeading={"Slet " + txt.getDynamic(`ActivityType_${props.activity.activityType}`) + "?"}
                                confirmationText={props.deleteConfirmationTextInModal ? props.deleteConfirmationTextInModal : ""}
                                requireKey={true}
                                onConfirmDelete={handleClickDeleteActivity} 
                                onCancel={() => setInDelete(false)} />
                        </>
                    }
                </ToolBarContainer>
            } />
        </>
    );
}

